import React, { Fragment, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { useStateValue } from '../../../hoc/state';
import { GlobalActionType, ModalT } from '../../../common/types';

const useStyles = makeStyles((theme) => ({
  modalShade: {
    position: 'absolute',
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    backgroundColor: '#2E2E2E',
    opacity: 0.5,
    zIndex: 50,
  },
  modalBody: {
    position: 'absolute',
    top: '72px',
    left: '0px',
    right: '0px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '1200px',
    height: '85%',
    backgroundColor: '#F9F9F9',
    zIndex: 51,
    borderRadius: '5px',
    '-webkit-box-shadow': '0px 3px 6px 0px rgba(0,0,0,0.16)',
    '-moz-box-shadow': '0px 3px 6px 0px rgba(0,0,0,0.16)',
    'box-shadow': '0px 3px 6px 0px rgba(0,0,0,0.16)',
  },
  modalContentHolder: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    overflowY: 'scroll',
  },

  modalContent: {
    padding: '33px 65px',
  },
  modalClose: {
    position: 'absolute',
    zIndex: 52,
    right: '57px',
    top: '50px',
    width: '25px',
    height: '25px',
    backgroundColor: '#2E2E2E',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '20px',
    textAlign: 'center',
    borderRadius: '2px',
    cursor: 'pointer',
    '-webkit-transition': '-webkit-transform .4s ease-in-out',
    transition: 'transform .4s ease-in-out',
    '&:hover': {
      opacity: 0.6,
    },
  },
  visible: {
    visibility: 'visible',
    opacity: 1,
    transition: 'opacity .1s linear',
  },

  hidden: {
    visibility: 'hidden',
    opacity: 0,
    transition: 'visibility 0s .5s, opacity .5s linear',
  },
}));

const Modal: React.FunctionComponent = () => {
  const { globalDispatch, modal } = useStateValue();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  const allowClose = modal ? modal.allowClose : true;
  const isOpen = modal && modal.on;

  const classes = useStyles();
  const isVisibile = isOpen ? classes.visible : classes.hidden;

  const closeModalObj: ModalT = {
    on: false,
    content: null,
    allowClose: true,
  };
  const closeModal = () => {
    globalDispatch({
      type: GlobalActionType.UPDATE_MODAL,
      payload: { modal: closeModalObj },
    });
  };

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      const timer = setTimeout(() => {
        if (modalBodyRef.current) {
          const focusable = modalBodyRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) {
            focusable[0].focus();
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      if (
        previousFocusRef.current &&
        previousFocusRef.current instanceof HTMLElement
      ) {
        previousFocusRef.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && allowClose) {
        closeModal();
        return;
      }
      if (e.key === 'Tab' && modalBodyRef.current) {
        const focusable = modalBodyRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allowClose]);

  return (
    <Fragment>
      <div
        className={classes.modalShade + ' ' + isVisibile}
        onClick={() => {
          if (allowClose) closeModal();
        }}
      />
      <div
        className={classes.modalBody + ' ' + isVisibile}
        ref={modalBodyRef}
        role="dialog"
        aria-modal="true"
        aria-label="Modal"
      >
        {allowClose ? (
          <div
            className={classes.modalClose}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
            onClick={() => {
              closeModal();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                closeModal();
              }
            }}
          >
            X
          </div>
        ) : null}
        <div className={classes.modalContentHolder}>
          <div className={classes.modalContent}>
            <div style={{ color: 'black' }}>
              {modal && modal.content ? <modal.content /> : null}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Modal;
